import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { authCookieExists } from "@entrilia/shared/utilities/cookies";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import HelpIcon from "@mui/icons-material/HelpOutlineRounded";
import { Button, Chip, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { api } from "../../api/client";
import useClientContext from "../../contexts/useClientContext";
import { cancelImpersonation, restoreImpersonationSearchParams } from "../../impersonation/impersonationHandler";
import { UserActions } from "../../store/state/user/actions";
import { impersonatedAccessToAllEntitiesSelector, userEmailSelector } from "../../store/state/user/selectors";
import { loadUserInfo } from "../../store/state/user/thunks";
import type { AppDispatch } from "../../store/store";
import ImpersonationHelpDialog from "./ImpersonationHelpDialog";
import ImpersonationSearchPanel from "./ImpersonationSearchPanel";

const PANEL_TEXT_COLOR = "info.contrastText";

const ImpersonationPanel = () => {
  const { clientCode, contactName } = useClientContext();
  const userEmail = useSelector(userEmailSelector);
  const impersonatedAccessToAllEntities = useSelector(impersonatedAccessToAllEntitiesSelector);

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));

  const [impersonationOptions, fetchError] = useFetch(api.impersonation.getImpersonationOptions);

  useEffect(() => {
    restoreImpersonationSearchParams(searchParams);
  }, [searchParams, location]);

  const handleStopInspectClick = () => {
    cancelImpersonation();
    if (!authCookieExists()) {
      navigate(`/${clientCode}/loggedoutImpersonation`);
    } else {
      dispatch(UserActions.UpdateImpersonation(false));
      dispatch(loadUserInfo(clientCode));
      navigate(`/${clientCode}`);
    }
  };

  const handleSearchPanelClose = () => {
    setShowSearchPanel(false);
  };

  if (fetchError) {
    logError(fetchError, "[ImpersonationPanel] getImpersonationOptions");
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{
          alignItems: "center",
          height: "3rem",
          left: "50%",
          bottom: "-1.5rem",
          position: "fixed",
          transform: "translate(-50%, -50%)",
          px: 4,
          py: 2,
          bgcolor: "info.main",
          borderRadius: "4px 4px 0px 0px",
          boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Typography noWrap sx={{ color: PANEL_TEXT_COLOR }}>
          You are inspecting the Investor Portal as&nbsp;
        </Typography>

        <Button
          variant="contained"
          color="info"
          endIcon={<ArrowDropDownIcon />}
          aria-haspopup="dialog"
          onClick={() => setShowSearchPanel(true)}
          sx={{ bgcolor: "info.dark", maxWidth: "262px" }}
        >
          <Typography component="span" variant="inherit" noWrap>
            {contactName}
          </Typography>
        </Button>

        <Button
          sx={{ color: PANEL_TEXT_COLOR, borderColor: PANEL_TEXT_COLOR, width: "8rem" }}
          variant="outlined"
          onClick={handleStopInspectClick}
        >
          Stop Inspect
        </Button>

        {!impersonatedAccessToAllEntities && (
          <Chip label="Entity-Level Access" color="warning" sx={{ fontWeight: 500, ml: 1 }} />
        )}

        <IconButton aria-label="help" sx={{ color: PANEL_TEXT_COLOR }} onClick={() => setShowHelpDialog(true)}>
          <HelpIcon fontSize="medium" />
        </IconButton>

        <Drawer
          anchor="right"
          open={showSearchPanel}
          onClose={handleSearchPanelClose}
          sx={(theme) => ({
            "& .MuiDrawer-paper": {
              p: 0,
              width: theme.spacing(78),
            },
          })}
        >
          <Stack sx={{ height: "100%" }}>
            <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", pl: 2, pr: 1, py: 2 }}>
              <Typography variant="subtitle1">User</Typography>
              <Typography color="textSecondary">{`${contactName} (${userEmail})`}</Typography>
              <CloseIconButton
                onClick={handleSearchPanelClose}
                sx={{ ml: "auto" }}
                iconProps={{ fontSize: "medium" }}
              />
            </Stack>

            {!!fetchError && <DataLoadingFailed />}

            {impersonationOptions === undefined && !fetchError && <InlineLoader />}

            {impersonationOptions !== undefined && !fetchError && (
              <ImpersonationSearchPanel email={userEmail} contactGroups={impersonationOptions.contactGroups} />
            )}
          </Stack>
        </Drawer>
      </Stack>
      <ImpersonationHelpDialog open={showHelpDialog} onClose={() => setShowHelpDialog(false)} />
    </>
  );
};

export default ImpersonationPanel;
